/**
 * VÉRAULT Portal — Sunset Decision Card (Jun 2026 · Belém)
 *
 * A calm, client-facing choice rendered inside the itinerary on the
 * Belém day. The client taps land vs FRS cruise, can reopen the
 * unselected option, and sees the advisor's recommendation + reasoning.
 * Selecting never books or charges — it records a preference and raises
 * an advisor task. Read-only in ghost / advisor-preview mode.
 */
import React from "react";
import { motion } from "framer-motion";
import { Sunset, Check, Star, ExternalLink, Loader2, Info } from "lucide-react";
import { api } from "../index";
import { usePreviewBase } from "../contexts/PreviewContext";

export default function SunsetDecisionCard({ tripId }) {
  const { isPreview } = usePreviewBase();
  const [data, setData] = React.useState(null);
  const [state, setState] = React.useState("loading");
  const [saving, setSaving] = React.useState(null);

  const load = React.useCallback(() => {
    const path = isPreview
      ? `/design-studio/${tripId}/sunset-decision`
      : `/client-portal/trips/${tripId}/sunset-decision`;
    api.get(path)
      .then(({ data }) => { setData(data); setState(data?.enabled ? "ready" : "off"); })
      .catch(() => setState("off"));
  }, [tripId, isPreview]);

  React.useEffect(() => { load(); }, [load]);

  const choose = async (choiceId) => {
    if (isPreview || saving) return;
    setSaving(choiceId);
    try {
      const { data: updated } = await api.post(
        `/client-portal/trips/${tripId}/sunset-decision`, { choice: choiceId });
      setData((d) => ({ ...d, ...updated }));
    } catch {
      /* keep the card interactive on failure */
    } finally {
      setSaving(null);
    }
  };

  if (state !== "ready" || !data) return null;

  const current = data.client_choice;
  const options = data.options || [];
  const chosenLabel = (options.find((o) => o.id === current) || {}).label;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl border border-[hsl(var(--accent-gold))]/35 bg-gradient-to-b from-[hsl(var(--accent-gold))]/[0.06] to-transparent p-5 sm:p-6 space-y-4"
      data-testid="sunset-decision-card"
    >
      <div className="flex items-start gap-2.5">
        <Sunset className="size-4 mt-0.5 text-[hsl(var(--accent-gold))] shrink-0" strokeWidth={1.5} />
        <div className="min-w-0">
          {data.eyebrow && (
            <p className="font-mono text-[9.5px] uppercase tracking-[0.2em] text-[hsl(var(--accent-gold))]">{data.eyebrow}</p>
          )}
          <h3 className="mt-1 font-serif text-[19px] sm:text-[21px] leading-snug">{data.title}</h3>
        </div>
      </div>

      {data.intro && (
        <p className="font-serif text-[14px] leading-relaxed text-[hsl(var(--foreground-secondary))]">{data.intro}</p>
      )}

      {/* Advisor recommendation + reasoning */}
      {data.advisor_recommendation_note && (
        <div className="rounded-lg bg-white/50 border border-[rgba(0,0,0,0.06)] p-3.5" data-testid="sunset-advisor-note">
          <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-[hsl(var(--accent-gold))] mb-1.5">Your advisor's take</p>
          <p className="font-accent italic text-[13px] leading-relaxed text-[hsl(var(--foreground-secondary))]">{data.advisor_recommendation_note}</p>
        </div>
      )}

      {/* Options */}
      <div className="grid sm:grid-cols-2 gap-3">
        {options.map((opt) => {
          const selected = current === opt.id;
          const busy = saving === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => choose(opt.id)}
              disabled={isPreview}
              data-testid={`sunset-option-${opt.id}`}
              aria-pressed={selected}
              className={`text-left rounded-lg border p-4 transition-all ${
                selected
                  ? "border-[hsl(var(--accent-gold))] bg-[hsl(var(--accent-gold))]/10 ring-1 ring-[hsl(var(--accent-gold))]/40"
                  : "border-[rgba(0,0,0,0.1)] bg-white/40 hover:border-[hsl(var(--accent-gold))]/60"
              } ${isPreview ? "cursor-default" : "cursor-pointer"}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-serif text-[15px] leading-snug">{opt.label}</span>
                {selected ? (
                  <span className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.16em] text-[hsl(var(--accent-gold))] shrink-0">
                    {busy ? <Loader2 className="size-3 animate-spin" strokeWidth={1.5} /> : <Check className="size-3" strokeWidth={2} />} Chosen
                  </span>
                ) : opt.recommended ? (
                  <Star className="size-3.5 text-[hsl(var(--accent-gold))] shrink-0" strokeWidth={1.5} fill="currentColor" />
                ) : null}
              </div>
              {opt.badge && (
                <p className={`mt-1.5 font-mono text-[8.5px] uppercase tracking-[0.14em] ${opt.recommended ? "text-[hsl(var(--accent-gold))]" : "text-[hsl(var(--foreground-muted))]"}`}>{opt.badge}</p>
              )}
              {opt.why && (
                <p className="mt-2 font-serif text-[12.5px] leading-relaxed text-[hsl(var(--foreground-secondary))]">{opt.why}</p>
              )}
              <div className="mt-2 flex items-center gap-3 flex-wrap">
                {opt.cost && (
                  <span className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-[hsl(var(--foreground-secondary))]">{opt.cost}</span>
                )}
                {opt.booking_link && (
                  <a href={opt.booking_link} target="_blank" rel="noreferrer noopener" onClick={(e) => e.stopPropagation()}
                     className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.14em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))]">
                    <ExternalLink className="size-3" strokeWidth={1.5} /> Details
                  </a>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Current state + reopen affordance */}
      {current ? (
        <p className="font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))]" data-testid="sunset-current-state">
          {current === "undecided"
            ? "You've let your advisor guide this one — tap either option any time to decide."
            : `Saved: ${chosenLabel}. Prefer the other? Tap it above — you can change your mind any time before the day.`}
        </p>
      ) : (
        <button
          type="button"
          onClick={() => choose("undecided")}
          disabled={isPreview}
          data-testid="sunset-option-undecided"
          className={`font-mono text-[9.5px] uppercase tracking-[0.16em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))] transition-colors ${isPreview ? "opacity-60 cursor-default" : ""}`}
        >
          Not sure yet — let my advisor guide this
        </button>
      )}

      {data.footnote && (
        <p className="flex items-start gap-1.5 font-mono text-[9px] uppercase tracking-[0.12em] text-[hsl(var(--foreground-muted))] pt-1 border-t border-[rgba(0,0,0,0.06)]">
          <Info className="size-3 mt-px shrink-0" strokeWidth={1.5} /> {data.footnote}
        </p>
      )}

      {isPreview && (
        <p className="font-mono text-[9px] uppercase tracking-[0.16em] text-[hsl(var(--foreground-muted))]" data-testid="sunset-preview-readonly">
          Ghost mode — read-only. Selecting is disabled in preview.
        </p>
      )}
    </motion.div>
  );
}
